import React, { useEffect, useState } from 'react';
import { FiEdit2, FiAlertCircle } from 'react-icons/fi';
import Modal from '../common/Modal';
import ButtonLoading from '../ButtonLoading';
import { userRequest } from '../../utils/requestMethods';
import { getFundEducationLevels } from '../../utils/enrollmentUtils';
import EducationLevelMultiSelect from './EducationLevelMultiSelect';

const EditFundModal = ({ isOpen, onClose, schoolId, fund, onUpdated, disabledLevels = {} }) => {
  const [name, setName] = useState(fund?.name || '');
  const [description, setDescription] = useState(fund?.description || '');
  const [educationalLevels, setEducationalLevels] = useState(getFundEducationLevels(fund));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(fund?.name || '');
    setDescription(fund?.description || '');
    setEducationalLevels(getFundEducationLevels(fund));
  }, [fund, isOpen]);

  const handleClose = () => {
    setError('');
    onClose();
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!name || name.trim().length < 2) {
      setError('Fund name must be at least 2 characters.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await userRequest.patch(`/schools/${schoolId}/funds/${fund._id}`, {
        name: name.trim(),
        description,
        educational_levels: educationalLevels,
      });
      onUpdated?.(data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update fund. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Fund" icon={<FiEdit2 className="text-indigo-600 text-lg" />}>
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-4">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <form onSubmit={submitHandler} className="space-y-4">
        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Fund Name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={120}
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Educational Levels (optional)</label>
          <EducationLevelMultiSelect
            value={educationalLevels}
            onChange={setEducationalLevels}
            disabledLevels={disabledLevels}
          />
          <p className="mt-1 text-xs text-gray-400">Changing these assignments updates future student payment routing.</p>
        </div>

        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            maxLength={500}
            rows={3}
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          />
          <p className="mt-1 text-xs text-gray-400 text-right">{description.length}/500</p>
        </div>

        {loading ? (
          <ButtonLoading styles="w-full text-white bg-indigo-500 font-medium rounded-full text-sm px-5 py-2.5 text-center" />
        ) : (
          <button
            type="submit"
            className="w-full text-white bg-indigo-600 hover:bg-indigo-700 font-medium rounded-full text-sm px-5 py-2.5 text-center transition-colors"
          >
            Save Changes
          </button>
        )}
      </form>
    </Modal>
  );
};

export default EditFundModal;
